/**
 * Entitlement snapshot (docs/05 "Resolution", docs/modules/entitlements.md).
 *
 * Computed per request by the entitlements module (EntitlementPort.snapshot) and shipped to
 * the SPA in `/v1/me`, so `authorize()` and `useCan()` answer identically. Code asks
 * `allows(featureKey)` / `remaining(featureKey)`; it never mentions a plan name.
 */
import type { MoneyJSON } from '@oex/kernel';

export type FeatureKind = 'boolean' | 'limit' | 'metered';
export type SubscriptionStatus = 'active' | 'trialing' | 'past_due' | 'cancelled';

export type FeatureEntitlement = {
  enabled: boolean;
  kind: FeatureKind;
  /** Units included per period; undefined = unlimited / not applicable. */
  included?: bigint;
  used?: bigint;
  /** Units left this period; undefined = unlimited / not applicable. */
  remaining?: bigint;
  hardCap?: boolean;
  overagePrice?: MoneyJSON;
};

export type EntitlementDenyReason = 'not_in_plan' | 'limit_reached';

export type EntitlementSnapshotData = {
  orgId: string;
  planCode: string;
  status: SubscriptionStatus;
  computedAt: string;
  features: Record<string, FeatureEntitlement>;
};

export interface EntitlementSnapshot extends EntitlementSnapshotData {
  /** Pure; used by authorize() and useCan(). */
  allows(featureKey: string): boolean;
  /** Units left: `null` = unlimited, `0n` = none (also for unknown features). */
  remaining(featureKey: string): bigint | null;
  /** Why `allows` is false, or null when allowed. */
  denyReason(featureKey: string): EntitlementDenyReason | null;
}

function evaluate(f: FeatureEntitlement | undefined): EntitlementDenyReason | null {
  if (!f || !f.enabled) return 'not_in_plan';
  switch (f.kind) {
    case 'boolean':
      return null;
    case 'limit':
      // enabled && (remaining > 0 || !hardCap)
      return f.remaining === undefined || f.remaining > 0n || !f.hardCap ? null : 'limit_reached';
    case 'metered':
      // enabled && (hardCap ? remaining > 0 : true)
      return !f.hardCap || f.remaining === undefined || f.remaining > 0n ? null : 'limit_reached';
  }
}

export function createEntitlementSnapshot(data: EntitlementSnapshotData): EntitlementSnapshot {
  const features = Object.freeze({ ...data.features });
  return Object.freeze({
    ...data,
    features,
    allows: (key: string) => evaluate(features[key]) === null,
    denyReason: (key: string) => evaluate(features[key]),
    remaining: (key: string) => {
      const f = features[key];
      if (!f || !f.enabled) return 0n;
      return f.remaining === undefined ? null : f.remaining;
    },
  });
}

/** Wire form: bigints as strings (docs/08). */
export type EntitlementSnapshotJSON = Omit<EntitlementSnapshotData, 'features'> & {
  features: Record<
    string,
    Omit<FeatureEntitlement, 'included' | 'used' | 'remaining'> & {
      included?: string;
      used?: string;
      remaining?: string;
    }
  >;
};

export function entitlementSnapshotToJSON(s: EntitlementSnapshotData): EntitlementSnapshotJSON {
  const features: EntitlementSnapshotJSON['features'] = {};
  for (const [k, f] of Object.entries(s.features)) {
    features[k] = {
      ...f,
      included: f.included?.toString(),
      used: f.used?.toString(),
      remaining: f.remaining?.toString(),
    };
  }
  return { orgId: s.orgId, planCode: s.planCode, status: s.status, computedAt: s.computedAt, features };
}

export function entitlementSnapshotFromJSON(json: EntitlementSnapshotJSON): EntitlementSnapshot {
  const big = (v: string | undefined) => (v === undefined ? undefined : BigInt(v));
  const features: Record<string, FeatureEntitlement> = {};
  for (const [k, f] of Object.entries(json.features)) {
    const { included, used, remaining, ...rest } = f;
    features[k] = {
      ...rest,
      ...(included !== undefined ? { included: big(included) } : {}),
      ...(used !== undefined ? { used: big(used) } : {}),
      ...(remaining !== undefined ? { remaining: big(remaining) } : {}),
    };
  }
  return createEntitlementSnapshot({ ...json, features });
}
